import React, { ElementType, useCallback } from 'react';

import { IconButton } from '../../../../shared/components/IconButton';
import { TablePagination as MUIPagination } from '@mui/material';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import arrowLeftIcon from '@ingka/ssr-icon/paths/arrow-left';
import arrowRightIcon from '@ingka/ssr-icon/paths/arrow-right';
import firstPageIcon from '@ingka/ssr-icon/paths/arrow-left-to-line';
import lastPageIcon from '@ingka/ssr-icon/paths/arrow-right-to-line';
import styled from '@emotion/styled';
import { tokens } from '../../../../shared/styles/core/tokens';
import { usePagination } from '../../state/pagination.state';

const CustomPagination: ElementType<TablePaginationActionsProps> = (props: TablePaginationActionsProps) => {
   const { count, page, rowsPerPage, onPageChange } = props;

   const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, 0);
   };

   const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page - 1);
   };

   const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page + 1);
   };

   const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
   };

   return (
      <StyledPaginationDiv>
         <StyledPaginationButtonDiv>
            <IconButton onClick={handleFirstPageButtonClick} title={'First Page'} disabled={page === 0} icon={firstPageIcon} />
         </StyledPaginationButtonDiv>

         <StyledPaginationButtonDiv>
            <IconButton onClick={handleBackButtonClick} title={'Previous Page'} disabled={page === 0} icon={arrowLeftIcon} />
         </StyledPaginationButtonDiv>

         <StyledPaginationButtonDiv>
            <IconButton
               onClick={handleNextButtonClick}
               disabled={page >= Math.ceil(count / rowsPerPage) - 1}
               icon={arrowRightIcon}
               title={'Next Page'}
            />
         </StyledPaginationButtonDiv>

         <StyledPaginationButtonDiv>
            <IconButton
               onClick={handleLastPageButtonClick}
               disabled={page >= Math.ceil(count / rowsPerPage) - 1}
               icon={lastPageIcon}
               title={'Last Page'}
            />
         </StyledPaginationButtonDiv>
      </StyledPaginationDiv>
   );
};

export const TablePagination = () => {

   const { nextPage, previousPage, gotoPage, setPageSize, pageIndex, pageSize, total } = usePagination();

   const onChangeRowsPerPage = (e: any) => {
      setPageSize(e.target.value);
   }

   const handleChangePage = useCallback(
      (_: any, newPage: number) => {
         const safePage = Math.floor(newPage);

         if (safePage === pageIndex + 1) {
            nextPage();
         } else if (safePage === pageIndex - 1) {
            previousPage();
         } else {
            gotoPage(safePage);
         }
      },
      [gotoPage, nextPage, pageIndex, previousPage]
   );

   return total ? (
      <StyledPagination
         rowsPerPageOptions={[10, 25, 50]}
         count={total}
         rowsPerPage={pageSize}
         page={Math.floor(pageIndex)}
         labelRowsPerPage=""
         onPageChange={handleChangePage}
         onRowsPerPageChange={onChangeRowsPerPage}
         ActionsComponent={CustomPagination}
         component={'div'}
      />
   ) : (
      <></>
   );
};

const StyledPaginationButtonDiv = styled.div`
   padding-left: ${tokens.space.md};
`;
const StyledPagination = styled(MUIPagination)`
   display: flex;
   justify-content: flex-end;
   align-items: center;
   width: 100%;
   background: transparent;
   min-height: unset;
   height: auto;

   .MuiTablePagination-toolbar {
      width: auto;
      margin: 0;
      padding: 0 ${tokens.space.sm};
      justify-content: flex-end;
      align-items: center;
      min-height: ${tokens.space.xl};
      height: ${tokens.space.xl};
      gap: ${tokens.space.xxs};
   }

   .MuiTablePagination-displayedRows {
      padding-right: 0;
      font-size: ${tokens.font.size.md};
      font-weight: ${tokens.font.weight.normal};
   }

   .MuiTablePagination-selectLabel,
   .MuiTablePagination-select {
      margin: 0 ${tokens.space.xxs};
   }
` as typeof MUIPagination;

const StyledPaginationDiv = styled.div`
   display: flex;
   justify-content: flex-end;
   width: 100%;
`;
